import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeGrid } from 'react-window';
import { useMemo, useRef, useState } from 'react';
import { cn } from 'src/utils/cn';
import DebouncedInput from 'src/components/DebouncedInput';

interface AdvanceSearchBoxProps {
  data: string[];
  dropSize: string;
  placeholder?: string;
  visibleRowSize: number;
  rowHeight: number;
  handleSelect: (item: string) => void;
}

export const AdvanceSearchBox = ({
  data,
  dropSize,
  rowHeight,
  visibleRowSize,
  placeholder = 'Search',
  handleSelect,
}: AdvanceSearchBoxProps) => {
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const gridRef = useRef<VariableSizeGrid>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const filteredData = useMemo(
    () =>
      data.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
    [query, data],
  );

  return (
    <div className="relative  min-w-full">
      <DebouncedInput
        className="p-1.5 border-1 border-gray-200 rounded-md min-w-full bg-slate-50 outline-1 focus:outline-1 focus:outline-blue-500 -outline-offset-1 outline-gray-300 text-sm"
        value={query}
        role="search"
        placeholder={placeholder}
        onChange={(e) => {
          setShowDropdown(e !== ''); // show dropdown on input change
          setQuery(e);
          setHighlightedIndex(0); // reset highlight on new search
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              Math.min(prev + 1, filteredData.length - 1),
            );
            gridRef.current?.scrollToItem({ rowIndex: highlightedIndex + 1 });
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            gridRef.current?.scrollToItem({ rowIndex: highlightedIndex - 1 });
          } else if (e.key === 'Enter') {
            e.preventDefault();
            handleSelect(filteredData[highlightedIndex]);
            setShowDropdown(false);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setShowDropdown(false);
          }
        }}
      />
      {showDropdown && (
        <div
          className={cn(
            'absolute z-10 mt-2 min-w-lg bg-white border-0 rounded-lg shadow-lg grow',
            dropSize,
          )}
        >
          <AutoSizer>
            {() => {
              const visibleRows = Math.min(filteredData.length, visibleRowSize);
              const gridHeight = rowHeight * visibleRows;
              return (
                <VariableSizeGrid
                  columnCount={1}
                  className="no-scrollbar"
                  columnWidth={() => 500}
                  height={gridHeight}
                  rowCount={filteredData.length}
                  rowHeight={() => rowHeight}
                  width={500}
                >
                  {({ rowIndex, style }) => {
                    const field = filteredData[rowIndex];
                    if (!field) return null;
                    const isHighlighted = rowIndex === highlightedIndex;

                    return (
                      <button
                        key={`${rowIndex}-${field}`}
                        id={`${rowIndex}-${field}`}
                        data-testid={`${rowIndex}-${field}`}
                        title={field}
                        className={`p-2 text-sm truncate text-left min-w-full cursor-pointer ${
                          isHighlighted ? 'bg-blue-100' : 'hover:bg-gray-100'
                        }`}
                        style={style}
                        onClick={() => {
                          handleSelect(field);
                          setQuery('');
                          setShowDropdown(false);
                        }}
                      >
                        {field}
                      </button>
                    );
                  }}
                </VariableSizeGrid>
              );
            }}
          </AutoSizer>
        </div>
      )}
    </div>
  );
};
